import React from 'react';
import _ from 'lodash';
import { TableColumn, Table, Tag, Icon } from '@scale/scaleui';
import { PodGroup, JobTableData, JobPostStatus } from '@scale/shared-types';
import { POD_GROUP_TAG } from 'react-scale/components/GrowthCenter/EmailDripCampaign/EmailDripCampaignTable';
import Link from '@scale/frontend-lib/link';
import { Routes } from 'react-scale/components/GrowthCenter/urls';

interface JobPostTableProps {
  data: JobTableData[];
  isLoading: boolean;
}

export const JobPostTable: React.FC<JobPostTableProps> = ({ data, isLoading }) => {
  const columns: TableColumn[] = [
    {
      id: 'id',
      header: 'ID',
      enableSorting: false,
      cell: function IdCell(props: any) {
        return (
          <a
            href={`https://app5.greenhouse.io/jobapps/${props.getValue()}/edit`}
            className="flex items-center"
            target="_blank"
            rel="noopener noreferrer"
          >
            {props.getValue()}
            <Icon icon="external-link-alt" className="text-xs ml-2" size="sm" />
          </a>
        );
      },
    },
    {
      id: 'name',
      header: 'Job Post',
      enableSorting: true,
      cell: function NameCell(props: any) {
        if (!props.row.original.id || !props.row.original.jobId) {
          return null;
        }
        return (
          <Link
            href={`${Routes.JobPostSetup}/${props.row.original.id}`}
            as={`/corp${Routes.JobPostSetup}/${props.row.original.id}`}
          >
            <a target="_blank" rel="noopener noreferrer">
              {_.truncate(props.getValue(), {
                length: 60,
              })}
            </a>
          </Link>
        );
      },
    },
    {
      id: 'ocpFlow',
      header: 'Onboarding Flow',
      cell: function OCPFlowCell(props: any) {
        const ocpFlow = props.getValue();
        const ocpFlowId = props.row.original.signupFlow?.onboardingFlow;
        return ocpFlow ? (
          <Link
            href={`${Routes.OnboardingControlPanel}/${ocpFlowId}`}
            as={`/corp${Routes.OnboardingControlPanel}/${ocpFlowId}`}
          >
            <a target="_blank" className="flex items-center" rel="noopener noreferrer">
              {_.truncate(ocpFlow, {
                length: 50,
              })}
              <Icon icon="external-link-alt" className="text-xs ml-2" size="sm" />
            </a>
          </Link>
        ) : null;
      },
    },
    {
      id: 'signupFlow',
      header: 'Signup Flow',
      cell: function SignupFlowCell(props: any) {
        const signupFlow = props.getValue();
        return signupFlow ? (
          <span>
            {_.truncate(signupFlow.name, {
              length: 35,
            })}
          </span>
        ) : null;
      },
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: true,
      cell: function StatusCell(props: any) {
        const value = props.getValue();
        return (
          <Tag
            variant={
              value === JobPostStatus.Public
                ? 'success'
                : value === JobPostStatus.Private
                ? 'info'
                : 'neutral'
            }
          >
            {value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : ''}
          </Tag>
        );
      },
    },
    {
      id: 'jobName',
      header: 'Internal Job Name',
      enableSorting: true,
      cell: function JobNameCell(props: any) {
        return (
          <a
            href={`https://app5.greenhouse.io/plans/${props.row.original.jobId}/jobapp`}
            className="flex items-center"
            target="_blank"
            rel="noopener noreferrer"
          >
            {_.truncate(props.getValue(), {
              length: 40,
            })}
            <Icon icon="external-link-alt" className="text-xs ml-2" size="sm" />
          </a>
        );
      },
    },
    {
      id: 'pod',
      header: 'Pod',
      enableSorting: true,
      cell: function DepartmentCell(props: any) {
        const pod = props.getValue();
        return (pod && <div>{POD_GROUP_TAG[pod as PodGroup]}</div>) || null;
      },
    },
    {
      id: 'referralReward',
      header: 'Referral Rate',
      enableSorting: true,
      cell: function ReferralRewardCell(props: any) {
        const reward = props.getValue();
        return <div className="flex items-center">{reward}</div>;
      },
    },
  ];

  return (
    <div className="w-full">
      <Table columns={columns} data={data || []} isLoading={isLoading} defaultPageSize={10} />
    </div>
  );
};
